// Auto-extracted from SellerProducts.jsx
import { useState, useEffect, useMemo } from "react";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { gradientForId } from "../../lib/gradients";
import { resolveImage } from "../../lib/images";
import { useCategories } from "../../hooks/useCategories";
import { STATUS_TABS, PRESET_VARIANT_TYPES, DELIVERY_METHODS, MAX_PHOTOS } from "./constants";
import { FieldLabel, Field, Section } from "./formHelpers";
import { VariantEditor } from "./VariantEditor";

export function ProductForm({ mode, product, seller, onClose, onPatch, onCreated, onDeleted }) {
  const isCreate = mode === "create";
  const { categories } = useCategories();
  const realCategories = categories.filter((c) => c.id !== "all");

  const priceKwacha = product?.price ? (product.price / 100).toFixed(2) : "";
  const compareKwacha = product?.compare_price ? (product.compare_price / 100).toFixed(2) : "";
  const costKwacha = product?.cost_price ? (product.cost_price / 100).toFixed(2) : "";
  const attrs = product?.attributes || {};
  const initialDelivery = Array.isArray(attrs.delivery_methods) ? attrs.delivery_methods : [];
  const initialUseStore = attrs.use_store_address !== false; // default true
  const initialPickup = attrs.pickup_address || seller?.address || "";

  const [form, setForm] = useState({
    title: product?.title || "",
    description: product?.description || "",
    category_id: product?.category_id || null,
    price: priceKwacha,
    compare_price: compareKwacha,
    cost_price: costKwacha,
    stock_qty: product?.stock_qty ?? "",
    status: product?.status || "active",
  });

  // Photos: array of { full, med, thumb } objects (new-schema) plus uploading state
  const initialPhotos = (() => {
    const imgs = product?.images;
    if (!imgs) return [];
    const arr = Array.isArray(imgs) ? imgs : [imgs];
    return arr
      .map((it) => {
        if (typeof it === "string") return { full: it, med: it, thumb: it, uploading: false };
        if (it && typeof it === "object") return { ...it, uploading: false };
        return null;
      })
      .filter(Boolean);
  })();
  const [photos, setPhotos] = useState(initialPhotos);

  const initialVariants = (() => {
    if (Array.isArray(attrs.variants) && attrs.variants.length > 0) {
      return attrs.variants
        .filter((v) => v && typeof v.type === "string" && Array.isArray(v.options))
        .map((v) => ({ type: v.type, options: [...v.options] }));
    }
    if (Array.isArray(product?.sizes) && product.sizes.length > 0) {
      return [{ type: "Size", options: [...product.sizes] }];
    }
    return [];
  })();
  const [variants, setVariants] = useState(initialVariants);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [variantCustomInput, setVariantCustomInput] = useState("");

  const [deliveryMethods, setDeliveryMethods] = useState(initialDelivery);
  const [useStoreAddress, setUseStoreAddress] = useState(initialUseStore);
  const [pickupAddress, setPickupAddress] = useState(initialPickup);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState(null);

  const priceNgwee = form.price ? Math.round(parseFloat(form.price) * 100) : 0;
  const compareNgwee = form.compare_price ? Math.round(parseFloat(form.compare_price) * 100) : 0;
  const costNgwee = form.cost_price ? Math.round(parseFloat(form.cost_price) * 100) : 0;

  const discountPct =
    compareNgwee > 0 && priceNgwee < compareNgwee
      ? ((compareNgwee - priceNgwee) / compareNgwee) * 100
      : 0;
  const profitNgwee = costNgwee > 0 && priceNgwee > 0 ? priceNgwee - costNgwee : 0;
  const profitPct = costNgwee > 0 && priceNgwee > 0 ? (profitNgwee / priceNgwee) * 100 : 0;

  const uploading = photos.some((p) => p.uploading);

  async function addPhoto(file) {
    if (!file || photos.length >= MAX_PHOTOS) return;
    const localId = Math.random().toString(36).slice(2, 9);
    // Optimistic: show local preview while uploading
    const previewUrl = URL.createObjectURL(file);
    setPhotos((p) => [...p, { localId, full: previewUrl, med: previewUrl, thumb: previewUrl, uploading: true }]);

    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const ownerId = seller.user_id || seller.id;
      const path = `${ownerId}/${Date.now()}-${localId}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("product-images")
        .upload(path, file, { upsert: false, contentType: file.type || `image/${ext}` });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("product-images").getPublicUrl(path);
      const url = data.publicUrl;
      setPhotos((p) =>
        p.map((ph) => (ph.localId === localId ? { full: url, med: url, thumb: url, uploading: false } : ph))
      );
    } catch (e) {
      setErr(`Upload failed: ${e.message || e}`);
      setPhotos((p) => p.filter((ph) => ph.localId !== localId));
    }
  }

  function removePhoto(index) {
    setPhotos((p) => p.filter((_, i) => i !== index));
  }

  function movePhoto(index, delta) {
    setPhotos((p) => {
      const next = [...p];
      const target = index + delta;
      if (target < 0 || target >= next.length) return p;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function toggleDelivery(id) {
    setDeliveryMethods((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  }

  function buildPayload() {
    const cleanVariants = variants
      .map((v) => ({ type: v.type, options: (v.options || []).filter(Boolean) }))
      .filter((v) => v.options.length > 0);
    const sizeVariant = cleanVariants.find((v) => v.type === "Size");
    const derivedSizes = sizeVariant ? sizeVariant.options : null;

    const nextAttrs = {};
    if (cleanVariants.length > 0) nextAttrs.variants = cleanVariants;
    if (deliveryMethods.length > 0) nextAttrs.delivery_methods = deliveryMethods;
    if (deliveryMethods.includes("pickup")) {
      nextAttrs.pickup_address = pickupAddress.trim() || null;
      nextAttrs.use_store_address = useStoreAddress;
    }

    const imagesPayload = photos
      .filter((p) => !p.uploading)
      .map((p) => ({ full: p.full, med: p.med, thumb: p.thumb }));

    return {
      title: form.title.trim(),
      description: form.description.trim() || null,
      category_id: form.category_id,
      price: priceNgwee,
      compare_price: compareNgwee || null,
      cost_price: costNgwee || null,
      stock_qty: Number(form.stock_qty) || 0,
      status: form.status,
      sizes: derivedSizes,
      attributes: Object.keys(nextAttrs).length > 0 ? nextAttrs : null,
      images: imagesPayload,
    };
  }

  async function save() {
    setErr(null);
    if (!form.title.trim() || form.title.trim().length < 3) {
      setErr("Title must be at least 3 characters");
      return;
    }
    if (!priceNgwee || priceNgwee <= 0) {
      setErr("Price must be greater than 0");
      return;
    }
    if (isCreate && !form.category_id) {
      setErr("Please pick a category");
      return;
    }
    if (uploading) {
      setErr("Wait for photos to finish uploading");
      return;
    }

    setSaving(true);
    const payload = buildPayload();

    if (isCreate) {
      const insertRow = {
        ...payload,
        seller_id: seller.id,
        type: "physical",
        total_sold: 0,
        view_count: 0,
      };
      const { data, error } = await supabase
        .from("products")
        .insert(insertRow)
        .select(
          "id, title, description, price, compare_price, cost_price, stock_qty, low_stock_threshold, images, status, total_sold, view_count, created_at, sizes, attributes, category_id, category:categories(name)"
        )
        .single();
      setSaving(false);
      if (error) {
        setErr(error.message);
        return;
      }
      onCreated(data);
      // Best-effort: bump seller.product_count
      supabase
        .from("sellers")
        .update({ product_count: (seller.product_count || 0) + 1 })
        .eq("id", seller.id)
        .then(() => {});
      onClose();
    } else {
      const patch = { ...payload, updated_at: new Date().toISOString() };
      const { error } = await supabase.from("products").update(patch).eq("id", product.id);
      setSaving(false);
      if (error) {
        setErr(error.message);
        return;
      }
      const cat = realCategories.find((c) => c.id === form.category_id);
      onPatch(product.id, { ...patch, category: cat ? { name: cat.name } : product.category });
    }
  }

  async function toggleStatus() {
    if (isCreate) {
      setForm((f) => ({ ...f, status: f.status === "active" ? "paused" : "active" }));
      return;
    }
    const next = form.status === "active" ? "paused" : "active";
    setForm((f) => ({ ...f, status: next }));
    const { error } = await supabase.from("products").update({ status: next }).eq("id", product.id);
    if (!error) onPatch(product.id, { status: next });
  }

  async function deleteProduct() {
    if (isCreate) return;
    setDeleting(true);
    setErr(null);
    const { error } = await supabase
      .from("products")
      .update({ status: "deleted", deleted_at: new Date().toISOString() })
      .eq("id", product.id);
    setDeleting(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onDeleted(product.id);
  }

  return (
    <div style={{ position: "sticky", top: 80, alignSelf: "start", maxHeight: "calc(100vh - 100px)", overflowY: "auto" }}>
      <Card pad={0}>
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${C.cream}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            background: C.white,
            zIndex: 2,
          }}
        >
          <Eyebrow>{isCreate ? "New product" : "Editing"}</Eyebrow>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4 }}
          >
            <Icon name="x" size={18} color={C.charcoal} />
          </button>
        </div>

        <div style={{ padding: 20, display: "grid", gap: 16 }}>
          {/* ============= PHOTOS ============= */}
          <Section title="Photos" hint={`Up to ${MAX_PHOTOS}. First photo is the cover.`}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
              {photos.map((ph, i) => (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    aspectRatio: "1",
                    borderRadius: 10,
                    overflow: "hidden",
                    background: C.cream,
                    border: i === 0 ? `2px solid ${C.hunter}` : `1px solid ${C.dustDk}`,
                  }}
                >
                  <img src={ph.thumb || ph.med || ph.full} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: ph.uploading ? 0.5 : 1 }} />
                  {ph.uploading && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: C.charcoal, background: "rgba(255,255,255,0.65)" }}>
                      Uploading…
                    </div>
                  )}
                  {i === 0 && !ph.uploading && (
                    <div style={{ position: "absolute", top: 4, left: 4, fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 5, background: C.hunter, color: C.white, letterSpacing: 0.5 }}>
                      COVER
                    </div>
                  )}
                  {!ph.uploading && (
                    <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 3 }}>
                      {i > 0 && (
                        <button onClick={() => movePhoto(i, -1)} style={iconBtn} title="Move left">
                          <Icon name="chevron-left" size={11} color={C.white} />
                        </button>
                      )}
                      {i < photos.length - 1 && (
                        <button onClick={() => movePhoto(i, 1)} style={iconBtn} title="Move right">
                          <Icon name="chevron-right" size={11} color={C.white} />
                        </button>
                      )}
                      <button onClick={() => removePhoto(i)} style={{ ...iconBtn, background: "rgba(239,68,68,0.85)" }} title="Remove">
                        <Icon name="x" size={11} color={C.white} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label
                  style={{
                    aspectRatio: "1",
                    borderRadius: 10,
                    border: `1.5px dashed ${C.hunter}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: C.hunter,
                    fontSize: 11,
                    fontWeight: 700,
                    gap: 4,
                    background: "transparent",
                  }}
                >
                  <Icon name="plus" size={18} color={C.hunter} />
                  Add photo
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      files.slice(0, MAX_PHOTOS - photos.length).forEach(addPhoto);
                      e.target.value = "";
                    }}
                    style={{ display: "none" }}
                  />
                </label>
              )}
            </div>
          </Section>

          {/* ============= BASIC INFO ============= */}
          <Section title="Basic info">
            <Field label="Title">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Vintage denim jacket"
                style={inputStyle}
              />
            </Field>
            <Field label="Description" style={{ marginTop: 10 }}>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What makes this item special? Fit, material, condition…"
                rows={4}
                style={{ ...inputStyle, resize: "vertical", minHeight: 80, fontFamily: "inherit" }}
              />
            </Field>
            <div style={{ marginTop: 10 }}>
              <FieldLabel>Category</FieldLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {realCategories.map((cat) => {
                  const on = form.category_id === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setForm({ ...form, category_id: cat.id })}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 20,
                        border: on ? "none" : `1.5px solid ${C.dustDk}`,
                        background: on ? C.hunter : C.white,
                        color: on ? C.white : C.charcoal,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {cat.icon ? `${cat.icon} ` : ""}
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </Section>

          {/* ============= PRICING + STOCK ============= */}
          <Section title="Pricing & stock">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Price (Kwacha)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  style={inputStyle}
                  placeholder="0.00"
                />
              </Field>
              <Field label="Compare price">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.compare_price}
                  onChange={(e) => setForm({ ...form, compare_price: e.target.value })}
                  style={inputStyle}
                  placeholder="0.00"
                />
                {discountPct > 0 && (
                  <div style={{ fontSize: 10.5, color: C.greenDk, marginTop: 4, fontWeight: 700 }}>
                    -{discountPct.toFixed(0)}% off
                  </div>
                )}
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <Field label="Cost price (your cost)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_price}
                  onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                  style={inputStyle}
                  placeholder="0.00"
                />
                {profitNgwee > 0 && (
                  <div style={{ fontSize: 10.5, color: C.greenDk, marginTop: 4, fontWeight: 700 }}>
                    +{fmtK(profitNgwee)} profit · {profitPct.toFixed(0)}% margin
                  </div>
                )}
              </Field>
              <Field label="Stock quantity">
                <input
                  type="number"
                  min="0"
                  value={form.stock_qty}
                  onChange={(e) => setForm({ ...form, stock_qty: e.target.value })}
                  style={inputStyle}
                  placeholder="0"
                />
              </Field>
            </div>
          </Section>

          {/* ============= VARIANTS ============= */}
          <Section title="Variants" hint="Optional: sizes, colors, storage, etc.">
            <VariantEditor
              variants={variants}
              setVariants={setVariants}
              showTypePicker={showTypePicker}
              setShowTypePicker={setShowTypePicker}
              editingVariant={editingVariant}
              setEditingVariant={setEditingVariant}
              variantCustomInput={variantCustomInput}
              setVariantCustomInput={setVariantCustomInput}
            />
          </Section>

          {/* ============= DELIVERY ============= */}
          <Section title="Delivery options" hint="Pick every way a buyer can receive this.">
            <div style={{ display: "grid", gap: 8 }}>
              {DELIVERY_METHODS.map((d) => {
                const on = deliveryMethods.includes(d.id);
                return (
                  <button
                    key={d.id}
                    onClick={() => toggleDelivery(d.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: on ? `1.5px solid ${C.hunter}` : `1.5px solid ${C.dustDk}`,
                      background: on ? C.greenBg : C.white,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: on ? C.hunter : C.cream,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon name={d.icon} size={14} color={on ? C.white : C.hunter} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>{d.label}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{d.desc}</div>
                    </div>
                    {on && <Icon name="check" size={14} color={C.hunter} />}
                  </button>
                );
              })}
            </div>

            {deliveryMethods.includes("pickup") && (
              <div style={{ marginTop: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: C.charcoal, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={useStoreAddress}
                    onChange={(e) => {
                      setUseStoreAddress(e.target.checked);
                      if (e.target.checked && seller?.address) setPickupAddress(seller.address);
                    }}
                  />
                  Use my store address
                </label>
                <textarea
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  disabled={useStoreAddress}
                  placeholder="Pickup address for this product"
                  rows={2}
                  style={{ ...inputStyle, marginTop: 6, fontFamily: "inherit", opacity: useStoreAddress ? 0.6 : 1 }}
                />
              </div>
            )}
          </Section>

          {err && (
            <div
              style={{
                fontSize: 12,
                padding: 10,
                background: C.redBg,
                color: C.red,
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              {err}
            </div>
          )}

          {/* ============= ACTIONS ============= */}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={save} disabled={saving || uploading} full>
              {saving ? "Saving…" : isCreate ? "Publish product" : "Save changes"}
            </Btn>
            <Btn kind="outline" onClick={toggleStatus}>
              {form.status === "active" ? "Pause" : "Activate"}
            </Btn>
          </div>

          {!isCreate && (
            <>
              <div
                style={{
                  borderTop: `1px solid ${C.cream}`,
                  paddingTop: 14,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  fontSize: 11.5,
                  color: C.muted,
                }}
              >
                <div>
                  <div style={{ fontWeight: 800, color: C.charcoal, fontSize: 13 }}>{product.view_count || 0}</div>
                  Views
                </div>
                <div>
                  <div style={{ fontWeight: 800, color: C.charcoal, fontSize: 13 }}>{product.total_sold || 0}</div>
                  Sold
                </div>
              </div>

              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: C.red,
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                    padding: 10,
                    fontFamily: "inherit",
                  }}
                >
                  Delete this product
                </button>
              ) : (
                <div style={{ background: C.redBg, borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: C.red, marginBottom: 8 }}>
                    Delete this product? It will be hidden from buyers.
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn kind="danger" size="sm" onClick={deleteProduct} disabled={deleting}>
                      {deleting ? "Deleting…" : "Yes, delete"}
                    </Btn>
                    <Btn kind="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </Btn>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

// ============= Shared primitives =============

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid rgba(196,193,183,0.4)",
  borderRadius: 10,
  fontSize: 13,
  fontFamily: "inherit",
  color: C.charcoal,
  background: C.white,
  outline: "none",
};

const iconBtn = {
  width: 22,
  height: 22,
  borderRadius: 5,
  border: "none",
  background: "rgba(0,0,0,0.55)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};
